import { addExceptionNotification } from '@mse-reg/app/modules/notification.slice';
import { createAppThunk } from '@mse-reg/app/store';
import { FileResponse, ISummarySheetClient, SummaryFilterDto } from '@mse-reg/clients';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { saveAs } from 'file-saver';

// State
const initialState = {
  loading: true,
  filter: null as SummaryFilterDto,
};

export type SummaryState = Readonly<typeof initialState>;

// Thunks
export const loadReportAsync = createAppThunk(
  'summary/loadReport',
  async (summaryFilter: SummaryFilterDto, deps: { summarySheetClient: ISummarySheetClient }, dispatch) => {
    try {
      const result = await deps.summarySheetClient.getSummarySheet(summaryFilter);
      saveAs(result.data, result.fileName);
      return result;
    } catch (e) {
      dispatch(addExceptionNotification(e));
      throw e;
    }
  },
  (r) => ({ summarySheetClient: r.resolve<ISummarySheetClient>('ISummarySheetClient') }),
);

// Slice
const summarySlice = createSlice({
  name: 'summary',
  initialState,
  reducers: {
    setSummaryFilter(state, action: PayloadAction<SummaryFilterDto>) {
      state.filter = action.payload;
      state.loading = false;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(loadReportAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadReportAsync.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(loadReportAsync.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const summaryReducer = summarySlice.reducer;
export const summaryActions = summarySlice.actions;
export const summaryThunks = { loadReportAsync };

// Legacy module pattern for backward compatibility
const initActions = (componentDispatch) => {
  return Object.freeze({
    setSummaryFilter: async (summaryFilter: SummaryFilterDto): Promise<void> =>
      componentDispatch(summarySlice.actions.setSummaryFilter(summaryFilter)),
    async loadReportAsync(summaryFilter: SummaryFilterDto): Promise<FileResponse | null> {
      try {
        return await componentDispatch(loadReportAsync(summaryFilter)).unwrap();
      } catch {
        return null;
      }
    },
  });
};

export type SummaryActions = ReturnType<typeof initActions>;

export const summaryModule = {
  initActions: (dispatch) => initActions(dispatch),
  reducer: summarySlice.reducer,
};
